import axiosInstance from './axiosConfig'

export type DriverSettlementDepositTarget = 'current_drawer' | 'other_vault'
export type DriverSettlementMoney = string | number

export type DriverSettlementReceiptLine = {
  id: string
  delivery_id: string
  sale_id?: string | number | null
  collected_amount: DriverSettlementMoney
  driver_earning: DriverSettlementMoney
  amount_due_from_driver: DriverSettlementMoney
  delivery?: {
    id: string
    delivered_at?: string | null
    collection_method?: string | null
    customer_phone?: string | null
  } | null
  sale?: {
    id: string | number
    invoice_number?: string | null
    sale_date?: string | null
    payment_type?: string | null
    total?: DriverSettlementMoney | null
    customer?: { id: string | number; name?: string | null; phone?: string | null } | null
  } | null
}

export type DriverSettlementReceiptData = {
  id: string
  branch_id: string
  driver_id: string
  shift_id?: string | null
  vault_id?: string | null
  period_from?: string | null
  period_to?: string | null
  deliveries_count: number
  total_collected: DriverSettlementMoney
  total_driver_earnings: DriverSettlementMoney
  total_due_from_driver: DriverSettlementMoney
  amount_received: DriverSettlementMoney
  difference: DriverSettlementMoney
  status: string
  notes?: string | null
  settled_at?: string | null
  created_at?: string | null
  client_uuid?: string | null
  branch?: { id: string; name: string; address?: string | null; phone?: string | null } | null
  driver?: { id: string; name: string; phone?: string | null } | null
  vault?: { id: string; name: string } | null
  shift?: {
    id: string
    shift_no?: number | null
    opened_at?: string | null
    closed_at?: string | null
  } | null
  creator?: { id: string | number; name: string } | null
  settlement_deliveries: DriverSettlementReceiptLine[]
  print_context?: {
    cashier_printer_name?: string | null
    profile?: 'thermal-80'
    roll_width_mm?: 80
  } | null
}

export type DriverSettlementResponse = {
  status: 'success' | 'error'
  message?: string
  data: DriverSettlementReceiptData
}

export type DriverSettlementPayload = {
  branch_id?: string | null
  driver_id: string
  delivery_ids: string[]
  amount_received: number
  deposit_target?: DriverSettlementDepositTarget
  confirm_physical_vault_deposit?: boolean
  vault_id?: string
  pos_register_id?: string
  register_session_id?: string
  notes?: string | null
  client_uuid?: string
}

export type DriverSettlementDepositContext = {
  open_shift: {
    id: string
    shift_no?: number | null
    vault_id?: string | null
    vault_name?: string | null
    drawer_ledger_enabled?: boolean
  } | null
  default_deposit_target: DriverSettlementDepositTarget
  available_vaults: Array<{ id: string; name: string }>
}

export type DriverSettlementQueueDriver = {
  driver_id: string
  driver_name: string
  driver_phone?: string | null
  deliveries_count: number
  total_collected: number
  total_driver_earnings: number
  total_due_from_driver: number
  oldest_delivered_at?: string | null
}

export type DriverSettlementQueuePayload = {
  drivers: DriverSettlementQueueDriver[]
  totals: {
    drivers_count: number
    deliveries_count: number
    total_due_from_driver: number
  }
  deposit_context: DriverSettlementDepositContext
  matched_driver_id?: string | null
}

export type DriverSettlementUnsettledRow = {
  delivery: {
    id: string
    collected_amount?: number | null
    driver_earning?: number | null
    amount_due_from_driver?: number | null
    delivered_at?: string | null
    customer_phone?: string | null
    sale?: {
      invoice_number?: string | null
      customer?: { name?: string | null; phone?: string | null } | null
    } | null
  }
  driver_earning_preview: number
  due_preview: number
}

export type DriverSettlementUnsettledPayload = {
  deliveries: DriverSettlementUnsettledRow[]
  totals: {
    count: number
    total_collected: number
    total_driver_earnings: number
    total_due_from_driver: number
  }
  deposit_context: DriverSettlementDepositContext
}

export type DriverSettlementListRow = {
  id: string
  driver_id: string
  deliveries_count: number
  total_due_from_driver: DriverSettlementMoney
  amount_received: DriverSettlementMoney
  difference: DriverSettlementMoney
  settled_at?: string | null
  driver?: { id: string; name: string; phone?: string | null } | null
}

export const driverSettlementsAPI = {
  queue: (params: { branch_id?: string; q?: string }) =>
    axiosInstance.get<{ status: string; data: DriverSettlementQueuePayload }>('/driver-settlements/queue', {
      params,
    }),

  unsettled: (params: Record<string, string | undefined>) =>
    axiosInstance.get<{ status: string; data: DriverSettlementUnsettledPayload }>(
      '/driver-settlements/unsettled',
      { params },
    ),

  list: (params: Record<string, string | number | undefined>) =>
    axiosInstance.get<{
      status: string
      data: { settlements: DriverSettlementListRow[]; pagination: Record<string, number> }
    }>('/driver-settlements', { params }),

  show: (id: string) => axiosInstance.get<DriverSettlementResponse>(`/driver-settlements/${id}`),

  create: (payload: DriverSettlementPayload) =>
    axiosInstance.post<DriverSettlementResponse>('/driver-settlements', payload),
}
